// Full resume list — the sidebar's "Resumes" destination and the dashboard's
// "View all" target. Same table as the dashboard plus a client-side search
// and an upload button (the dropzone stays on the dashboard).

import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardPaste, Inbox, Search, Upload } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { RESUME_ACCEPT, useResumeUpload } from "@/hooks/useResumeUpload";
import type { JobListItem } from "@/lib/jobs";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { ResumeTable } from "@/components/ResumeTable";
import { UploadStage } from "@/components/UploadStage";
import { PasteStage } from "@/components/PasteStage";

export function Resumes() {
  const api = useApi();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [pasting, setPasting] = useState(false);
  const { handleFiles, pasteText, pending, setPending, confirmPending, cancelPending, uploading, error } =
    useResumeUpload();

  const jobsQuery = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get<{ jobs: JobListItem[] }>("/api/resume-jobs"),
    refetchInterval: (query) =>
      query.state.data?.jobs.some((j) => j.status === "extracting") ? 2000 : false,
  });

  // Confirmation lives in ResumeTable's dialog; this only runs after a yes.
  const deleteJob = useCallback(
    async (id: string) => {
      await api.delete(`/api/resume-jobs/${id}`);
      void jobsQuery.refetch();
    },
    [api, jobsQuery],
  );

  const jobs = jobsQuery.data?.jobs;
  const filtered = useMemo(() => {
    if (!jobs) return [];
    const q = search.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(
      (j) =>
        (j.candidateName ?? "").toLowerCase().includes(q) ||
        j.sourceFilename.toLowerCase().includes(q) ||
        j.ownerEmail.toLowerCase().includes(q),
    );
  }, [jobs, search]);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="mb-1">Resumes</h2>
          <p className="text-[14px] text-text-2">
            Every uploaded resume{jobs ? ` — ${jobs.length} total` : ""}. Open one to review or
            generate documents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPasting((v) => !v)} disabled={uploading}>
            <ClipboardPaste size={15} strokeWidth={1.75} aria-hidden />
            Paste text
          </Button>
          <Button onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Upload size={15} strokeWidth={1.75} aria-hidden />
            {uploading ? "Uploading…" : "Upload resume"}
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={RESUME_ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </header>

      {error && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {error}
        </p>
      )}
      {pasting && (
        <PasteStage
          onConfirm={(text) => void pasteText(text)}
          onCancel={() => setPasting(false)}
          busy={uploading}
        />
      )}
      {pending && (
        <UploadStage
          files={pending}
          onChange={setPending}
          onConfirm={confirmPending}
          onCancel={cancelPending}
          uploading={uploading}
        />
      )}

      {!jobsQuery.isLoading && (jobs?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<Inbox className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
          title="No resumes yet"
          body="Upload the first candidate resume — extraction usually takes under 30 seconds."
          action={
            <Button onClick={() => inputRef.current?.click()}>
              <Upload size={15} strokeWidth={1.75} aria-hidden />
              Upload resume
            </Button>
          }
        />
      ) : (
        <Card>
          <CardBody className="px-0 py-0">
            <div className="flex items-center gap-2 border-b border-border px-5 py-3">
              <Search size={15} strokeWidth={1.75} className="shrink-0 text-text-2" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by candidate, file or uploader…"
                aria-label="Search resumes"
                className="h-8 max-w-sm border-0 bg-transparent px-1 focus:outline-none"
              />
            </div>
            {!jobsQuery.isLoading && filtered.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-text-2">
                Nothing matches “{search}”.
              </p>
            ) : (
              <ResumeTable
                jobs={filtered}
                loading={jobsQuery.isLoading}
                onDelete={(id) => void deleteJob(id)}
              />
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
